import { useRouter } from "next/router";
import React, { createContext, useContext, useEffect } from "react";
import { useBusiness } from "../../business/provider";
import { ActionType, CustomTemplate, VariableType } from "./models";
import {
  useWhatsappTemplateState,
  ValidationError,
  ExtendedTemplate,
} from "./provider-state";
import { DbBusinessWhatsappCustomHeader } from "../custom-header/models";
import { whatsAppCustomHeaderService } from "../custom-header/service";
import { whatsAppCustomTemplateService } from "./service";

interface WhatsappTemplateContextProps {
  addCustomTemplateCurrentStep: number;
  setAddCustomTemplateCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  editCustomTemplateCurrentStep: number;
  setEditCustomTemplateCurrentStep: React.Dispatch<
    React.SetStateAction<number>
  >;
  basicCustomTemplateDetails: CustomTemplate;
  setBasicCustomTemplateDetails: React.Dispatch<
    React.SetStateAction<CustomTemplate>
  >;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  successMessage: string | null;
  setSuccessMessage: React.Dispatch<React.SetStateAction<string | null>>;
  errors: { for: string; message: string }[];
  setErrors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step1Errors: { for: string; message: string }[];
  setStep1Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  step2Errors: ValidationError[];
  setStep2Errors: React.Dispatch<React.SetStateAction<ValidationError[]>>;
  step3Errors: { for: string; message: string }[];
  setStep3Errors: React.Dispatch<
    React.SetStateAction<{ for: string; message: string }[]>
  >;
  createCustomTemplate: () => Promise<void>;
  editCustomTemplate: () => Promise<void>;
  clearCustomTemplateDetails: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  actionType: ActionType;
  setActionType: React.Dispatch<React.SetStateAction<ActionType>>;
  customHeader: DbBusinessWhatsappCustomHeader | null;
  setCustomHeader: React.Dispatch<
    React.SetStateAction<DbBusinessWhatsappCustomHeader | null>
  >;
  currentVariableType: VariableType;
  setCurrentVariableType: React.Dispatch<React.SetStateAction<VariableType>>;
  templates: ExtendedTemplate[];
  setTemplates: React.Dispatch<React.SetStateAction<ExtendedTemplate[]>>;
  imageUrlMap: Record<string, string>;
  setImageUrlMap: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

const WhatsappTemplateContext =
  createContext<WhatsappTemplateContextProps | null>(null);

export default function WhatsappTemplateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const { selectedBusiness } = useBusiness();
  const state = useWhatsappTemplateState();

  const {
    resetState,
    customHeader,
    setCustomHeader,
    basicCustomTemplateDetails,
    setIsLoading,
    setError,
    setSuccessMessage,
    templates,
    setTemplates,
    isLoading,
  } = state;

  useEffect(() => {
    if (!selectedBusiness) return;
    const fetchCustomHeader = async () => {
      try {
        const customHeader =
          await whatsAppCustomHeaderService.getWhatsAppCustomHeader(
            selectedBusiness.id,
          );
        setCustomHeader(customHeader);
      } catch (_error) {
        setError("Something went wrong");
      }
    };
    fetchCustomHeader();
  }, [selectedBusiness, setCustomHeader, setError]);

  const createCustomTemplate = async () => {
    try {
      setIsLoading(true);
      await whatsAppCustomTemplateService.createCustomTemplate(
        basicCustomTemplateDetails,
      );
      resetState();
      router.push("/whatsapp/message-templates");
    } catch {
      state.setError("Failed to create template");
    } finally {
      setIsLoading(false);
    }
  };

  const editCustomTemplate = async () => {
    try {
      setIsLoading(true);
      await whatsAppCustomTemplateService.editCustomTemplate(
        basicCustomTemplateDetails,
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setSuccessMessage("You needs to wait for a while to see the changes.");
      await router.push("/whatsapp/message-templates");
      resetState();
    } catch {
      setError("Failed to update template");
    } finally {
      setIsLoading(false);
    }
  };

  const clearCustomTemplateDetails = () => {
    resetState();
  };

  return (
    <WhatsappTemplateContext.Provider
      value={{
        ...state,
        createCustomTemplate,
        editCustomTemplate,
        clearCustomTemplateDetails,
        customHeader,
        templates,
        setTemplates,
      }}
    >
      {children}
    </WhatsappTemplateContext.Provider>
  );
}

export const useWhatsappTemplate = () => {
  const context = useContext(WhatsappTemplateContext);

  if (!context) {
    throw new Error(
      "useWhatsappTemplate must be used within WhatsappTemplateProvider",
    );
  }
  return context;
};
